import React, { createContext, useContext, useState, ReactNode, useMemo } from 'react';
import { UserType } from '@/types';
import { useTranslation } from 'react-i18next';

interface AppContextState {
  step: number;
  setStep: (step: number) => void;
  userType: UserType;
  setUserType: (userType: UserType) => void;
  error: string;
  setError: (error: string) => void;
  company: string;
  setCompany: (company: string) => void;
  phone: string;
  setPhone: (phone: string) => void;
  barcode: string;
  setBarcode: (barcode: string) => void;
  lockerSize: string;
  setLockerSize: (size: string) => void;
  lockerType: string;
  setLockerType: (type: string) => void;
  holdTime: number;
  setHoldTime: (time: number) => void;
  amount: number;
  setAmount: (amount: number) => void;
  assignedLocker: string;
  setAssignedLocker: (locker: string) => void;
  otp: string;
  setOTP: (otp: string) => void;
  language: string;
  setLanguage: (lang: string) => void;
  assignmentId: string;
  setAssignmentId: (id: string) => void;
}

export const AppContext = createContext<AppContextState | undefined>(undefined);

interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [step, setStep] = useState(0);
  const [userType, setUserType] = useState<UserType>(null);
  const [error, setError] = useState('');
  const [company, setCompany] = useState('');
  const [phone, setPhone] = useState('');
  const [barcode, setBarcode] = useState('');
  const [lockerSize, setLockerSize] = useState('');
  const [lockerType, setLockerType] = useState('');
  const [holdTime, setHoldTime] = useState(0);
  const [amount, setAmount] = useState(0);
  const [assignedLocker, setAssignedLocker] = useState('');
  const [otp, setOTP] = useState('');
  const { i18n } = useTranslation();
  const [language, setLanguage] = useState(i18n.language);
  const [assignmentId, setAssignmentId] = useState('');

  const setAppLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    setLanguage(lang);
  };

  const value = useMemo(() => ({
    step,
    setStep,
    userType,
    setUserType,
    error,
    setError,
    company,
    setCompany,
    phone,
    setPhone,
    barcode,
    setBarcode,
    lockerSize,
    setLockerSize,
    lockerType,
    setLockerType,
    holdTime,
    setHoldTime,
    amount,
    setAmount,
    assignedLocker,
    setAssignedLocker,
    otp,
    setOTP,
    language,
    setLanguage: setAppLanguage,
    assignmentId,
    setAssignmentId,
  }), [
    step, userType, error, company, phone, barcode,
    lockerSize, lockerType, holdTime, amount, assignedLocker, otp, language, i18n
  ]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useAppContext = (): AppContextState => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};

export default AppContext;
